import type { SerializedFile } from "@repo/models";

import { downloadFile } from "@repo/helpers";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	Button,
	ButtonTypes,
	FileUploader,
} from "@repo/ui";
import { useCallback, useState } from "react";

import DrugTestsApi from "../../../api/drugTestsApi";
import RepsApi from "../../../api/repsApi";

interface Props {
	visible: boolean;
	onClose: () => void;
	onSuccess: () => void;
}

export function BulkImportModal({ visible, onClose, onSuccess }: Props) {
	const [files, setFiles] = useState<SerializedFile[]>([]);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [errors, setErrors] = useState<string>("");

	const handleDownloadTemplate = useCallback(async () => {
		try {
			const blob = await DrugTestsApi.downloadBulkUploadTemplate();
			const url = window.URL.createObjectURL(blob);
			downloadFile(url, "bulk-upload-template.xlsx");
		} catch (e) {
			console.error("Failed to download template", e);
		}
	}, []);

	const handleSubmit = useCallback(async () => {
		if (files.length === 0) return;

		setIsSubmitting(true);
		setErrors("");

		try {
			const formData = new FormData();
			const file = files[0];
			if (file.url) {
				const res = await fetch(file.url);
				const blob = await res.blob();
				formData.append("bulkUploadFile", blob, file.name);
			}

			const result = await DrugTestsApi.processBulkImportFile(formData);

			if (result.errorLog) {
				setErrors(result.importLog + "\n" + result.errorLog);
			} else {
				setErrors(result.importLog);
				onSuccess();
				// The MVC code doesn't close on success, just shows logs.
			}
		} catch (e: unknown) {
			setErrors("An unexpected error occurred during import.");
			console.error(e);
		} finally {
			setIsSubmitting(false);
		}
	}, [files, onSuccess]);

	const renderBody = () => (
		<div className="flex flex-col gap-4 p-4">
			<div className="text-center">
				<FileUploader
					id="bulkUploadFile"
					label="Select Bulk Import File"
					onFilesAdded={(_, newFiles) => setFiles(newFiles)}
					files={files}
					acceptFileTypes={[".xlsx"]}
					multiple={false}
					autoReplace={true}
				/>
			</div>

			<div className="text-center">
				<Button onClick={handleDownloadTemplate} types={[ButtonTypes.Primary]}>
					Download the import template
				</Button>
			</div>

			{errors && (
				<textarea
					readOnly
					value={errors}
					style={{ width: "100%", height: "170px", color: "red", border: "1px solid red", padding: "8px" }}
				/>
			)}
		</div>
	);

	return (
		<AlertDialog open={visible} onOpenChange={(open) => !open && onClose()}>
			<AlertDialogContent style={{ maxWidth: "500px" }}>
				<AlertDialogHeader>
					<AlertDialogTitle>Bulk Drug Test Import</AlertDialogTitle>
				</AlertDialogHeader>

				<AlertDialogDescription asChild>{renderBody()}</AlertDialogDescription>

				<AlertDialogFooter>
					<AlertDialogAction
						onClick={handleSubmit}
						types={[ButtonTypes.Primary]}
						disabled={isSubmitting || files.length === 0}
					>
						{isSubmitting ? "Submitting..." : "Submit"}
					</AlertDialogAction>
					<AlertDialogCancel onClick={onClose} types={[ButtonTypes.Outline]}>
						Cancel
					</AlertDialogCancel>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
}
